import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { useGroupRelationship } from "@/api/group-relationships";
import { useCancelMembershipRequest, useJoinGroup } from "@/api/groups";
import { Button, Divider, HStack, Popover, Stack, Text } from "@/components/ui";
import GroupMemberCount from "@/features/group/components/group-member-count";
import GroupPrivacy from "@/features/group/components/group-privacy";
import { useIsTouchDevice } from "@/hooks";
import { cn } from "@/utils/cn";
import { isGroupAccessRequired } from "@/utils/groups";

import GroupAvatar from "../groups/group-avatar";

import type { Group } from "@/schemas";

interface GroupPopoverContainerProps {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  isEnabled: boolean;
  interaction?: React.ComponentProps<typeof Popover>["interaction"];
  group: Group;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  showAction?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

const messages = defineMessages({
  requiredTitle: {
    id: "group.popover.title",
    defaultMessage: "Membership required",
  },
  requiredSummary: {
    id: "group.popover.summary",
    defaultMessage:
      "You must be a member of the group in order to interact with this Truth.",
  },
  view: { id: "group.popover.view", defaultMessage: "View Group" },
  action: { id: "group.popover.action", defaultMessage: "Request Access" },
  cancelRequest: {
    id: "group.popover.cancel",
    defaultMessage: "Cancel Request",
  },
  join: { id: "group.popover.join", defaultMessage: "Join Group" },
});

const GroupPopover = (props: GroupPopoverContainerProps) => {
  const {
    children,
    group,
    isEnabled,
    interaction = "click",
    isOpen,
    onOpenChange,
    showAction = false,
    ref,
  } = props;
  const joinGroup = useJoinGroup();
  const cancelRequest = useCancelMembershipRequest();
  const isPredictionGroup = !!group?.event_category_name;

  const intl = useIntl();
  const isTouchDevice = useIsTouchDevice();
  const { groupRelationship } = useGroupRelationship(group?.id);

  const accessRequired = isGroupAccessRequired(group);

  const handleRequestAccess = () => {
    joinGroup.mutate({ id: group.id });
  };

  const handleCancelRequest = () => {
    cancelRequest.mutate({
      group,
      id: group.id,
    });
  };

  if (isTouchDevice || !isEnabled || !group) {
    return children;
  }

  const renderRequestButtons = () => {
    return groupRelationship?.requested ? (
      <Button
        type="button"
        variant="secondary"
        block
        onClick={handleCancelRequest}
      >
        {intl.formatMessage(messages.cancelRequest)}
      </Button>
    ) : (
      <Button
        type="button"
        variant="default"
        block
        onClick={handleRequestAccess}
      >
        {intl.formatMessage(messages.action)}
      </Button>
    );
  };

  const renderJoinButtons = () => {
    return (
      <Button
        type="button"
        variant="default"
        block
        onClick={handleRequestAccess}
      >
        {intl.formatMessage(messages.join)}
      </Button>
    );
  };

  return (
    <Popover
      interaction={interaction}
      referenceElementClassName="cursor-pointer"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      content={
        <Stack space={4} className="w-80 pb-4" data-testid="group-popover">
          <Stack
            className="relative rounded-lg bg-white dark:border-gray-800 dark:bg-gray-900"
            data-testid="group-card"
          >
            {/* Group Cover Image */}
            <Stack className="relative h-32 rounded-t-lg bg-primary-200 dark:bg-gray-800">
              {group.header_static && (
                <img
                  className="absolute inset-0 size-full rounded-t-lg object-cover"
                  src={group.header_static}
                  alt=""
                />
              )}
            </Stack>

            {/* Group Avatar */}
            {!isPredictionGroup && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <GroupAvatar group={group} size={64} withRing />
              </div>
            )}

            {/* Group Info */}
            <Stack
              alignItems="center"
              justifyContent="end"
              grow
              className={cn("mt-auto pt-10", { "pt-4": isPredictionGroup })}
              space={0.5}
            >
              <div className="px-4">
                <Text
                  size="lg"
                  weight="bold"
                  align="center"
                  dangerouslySetInnerHTML={{
                    __html: group.display_name_html,
                  }}
                />
              </div>
              {!isPredictionGroup && (
                <HStack className="text-muted-foreground" space={2} wrap>
                  <GroupPrivacy group={group} />
                  <GroupMemberCount group={group} />
                </HStack>
              )}
            </Stack>
          </Stack>

          <Stack space={4} className="px-4">
            {showAction ? (
              <>
                <Divider />

                {/* Membership Required Text */}
                <Stack space={2} className="px-4">
                  <Text weight="semibold">
                    {intl.formatMessage(messages.requiredTitle)}
                  </Text>
                  <Text theme="muted" size="sm">
                    {intl.formatMessage(messages.requiredSummary)}
                  </Text>
                </Stack>

                {/* Request Access or Join Group Button */}
                {accessRequired ? renderRequestButtons() : renderJoinButtons()}
              </>
            ) : (
              <Link
                to={`/group/${group.slug}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* View Group Button */}
                <Button type="button" variant="secondary" block>
                  {intl.formatMessage(messages.view)}
                </Button>
              </Link>
            )}
          </Stack>
        </Stack>
      }
      isFlush
      children={<div className="inline-block">{children}</div>}
    />
  );
};

export default GroupPopover;
